import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, Clock, CurrencyDollar, ArrowUpRight, CheckCircle } from "@phosphor-icons/react";
import { Reveal } from "@/components/site/Reveal";
import { getJob } from "@/lib/api";

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getJob(id)
      .then(setJob)
      .catch(() => setJob(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-[60vh] grid place-items-center pt-32">
        <p className="text-slate-500" data-testid="job-detail-loading">Loading…</p>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-[60vh] grid place-items-center pt-32 px-5 text-center" data-testid="job-not-found">
        <div>
          <h1 className="font-display font-black text-3xl text-navy-900">This role isn't available.</h1>
          <p className="text-slate-500 mt-2">It may have been filled or closed.</p>
          <button
            onClick={() => navigate("/careers")}
            className="inline-flex items-center gap-2 mt-6 rounded-full bg-navy-900 text-white px-6 py-3 font-bold hover:bg-navy-600 transition-colors"
          >
            <ArrowLeft size={16} /> Back to careers
          </button>
        </div>
      </div>
    );
  }

  const checklist = [
    "Your contact info & home address",
    "Work eligibility & driver's license",
    "Employment history & education",
    "A couple of references",
    "Resume (optional)",
  ];

  return (
    <div data-testid="job-detail-page">
      {/* Hero */}
      <section className="relative bg-navy-900 text-white pt-36 pb-16 md:pt-44 md:pb-20 overflow-hidden grain">
        <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-navy-600/30 blur-3xl" />
        <div className="relative max-w-[1000px] mx-auto px-5 md:px-10">
          <Link to="/careers" data-testid="job-back-link" className="inline-flex items-center gap-2 text-sm text-navy-300 hover:text-white mb-6">
            <ArrowLeft size={16} /> All open roles
          </Link>
          <span className="block text-xs font-bold uppercase tracking-[0.2em] text-navy-400">Careers</span>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-3 leading-[1.05]">
            {job.title}
          </h1>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-6 text-slate-300">
            <span className="inline-flex items-center gap-1.5"><MapPin size={18} weight="fill" className="text-navy-400" />{job.location}</span>
            <span className="inline-flex items-center gap-1.5"><Clock size={18} weight="fill" className="text-navy-400" />{job.job_type}</span>
            {job.pay_range && (
              <span className="inline-flex items-center gap-1.5"><CurrencyDollar size={18} weight="fill" className="text-navy-400" />{job.pay_range}</span>
            )}
          </div>
          <Link
            to={`/careers/${job.id}/apply`}
            data-testid="apply-now-btn"
            className="group inline-flex items-center gap-2 mt-8 rounded-full bg-white text-navy-900 pl-7 pr-5 py-4 font-bold hover:bg-slate-100 transition-colors"
          >
            Apply Now
            <span className="w-8 h-8 grid place-items-center rounded-full bg-navy-900 text-white group-hover:rotate-45 transition-transform">
              <ArrowUpRight size={18} weight="bold" />
            </span>
          </Link>
        </div>
      </section>

      {/* Body */}
      <section className="max-w-[1000px] mx-auto px-5 md:px-10 py-16 md:py-20 grid lg:grid-cols-[1.5fr_1fr] gap-12">
        <Reveal>
          <div data-testid="job-description" className="prose prose-slate max-w-none">
            <h2 className="font-display font-black text-2xl text-navy-900 tracking-tight">About the role</h2>
            <p className="text-slate-600 mt-4 whitespace-pre-wrap leading-relaxed">{job.description}</p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="lg:sticky lg:top-28">
            <div className="bg-white border border-slate-200 rounded-3xl p-6 md:p-7 shadow-[0_12px_40px_rgb(10,17,40,0.06)]">
              <h3 className="font-display font-black text-xl text-navy-900 tracking-tight">Ready to apply?</h3>
              <p className="text-slate-500 text-sm mt-1.5">The application takes about 5 minutes. Have this handy:</p>
              <ul className="mt-4 space-y-2.5">
                {checklist.map((c) => (
                  <li key={c} className="flex items-start gap-2.5 text-sm text-slate-700">
                    <CheckCircle size={18} weight="fill" className="text-navy-600 shrink-0 mt-0.5" /> {c}
                  </li>
                ))}
              </ul>
              <Link
                to={`/careers/${job.id}/apply`}
                data-testid="apply-now-btn-sidebar"
                className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-navy-900 text-white py-3.5 font-bold hover:bg-navy-600 transition-colors"
              >
                Start application <ArrowUpRight weight="bold" />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
